import { router } from '@/plugins/1.router'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'


export const useAuthStore = defineStore('auth', () => {
  const loading = ref(false)
  const error = ref(null)

  const accessTokenCookie = useCookie('accessToken', { default: () => '' })
  const userDataCookie = useCookie('userData', { default: () => null })
  const userRoleCookie = useCookie('userRole', { default: () => '' })
  const userAbilitiesCookie = useCookie('userAbilityRules', { default: () => [] })
  const userProfileCookie = useCookie('userProfile', { default: () => null })
  
  // 🆕 Cookie per dati anestesista
  const anesthesiologistDataCookie = useCookie('anesthesiologistData', { default: () => null })

  const extractErrorMessage = err => {
    if (err?.data?.message)
      return err.data.message
    if (err?.response?._data?.message)
      return err.response._data.message
    if (typeof err?.message === 'string')
      return err.message

    return 'An unexpected error occurred.'
  }

  const persistAuthPayload = payload => {
    const resolvedToken = payload?.accessToken || payload?.access_token || ''
    const resolvedUserData = payload?.userData || payload?.user || null
    const resolvedRole = resolvedUserData?.role || payload?.role || ''

    accessTokenCookie.value = resolvedToken
    userDataCookie.value = resolvedUserData
    userRoleCookie.value = resolvedRole
    userAbilitiesCookie.value = payload?.userAbilityRules || []
    userProfileCookie.value = payload?.profile || null
    
    // 🆕 Salva i dati anestesista se presenti
    anesthesiologistDataCookie.value = payload?.userData?.anesthesiologist || null

    console.log('[auth] persisted', {
      tokenLen: accessTokenCookie.value?.length ?? 0,
      role: userRoleCookie.value,
      hasUser: Boolean(userDataCookie.value),
      hasAnesthesiologist: Boolean(anesthesiologistDataCookie.value),
    })
  }

  const clearAuthPayload = () => {
    accessTokenCookie.value = ''
    userDataCookie.value = null
    userRoleCookie.value = ''
    userAbilitiesCookie.value = []
    userProfileCookie.value = null
    anesthesiologistDataCookie.value = null
    error.value = null
  }

  const clearError = () => {
    error.value = null
  }

  const login = async ({ email, password, remember = false }) => {
    clearAuthPayload()
    loading.value = true
    error.value = null

    try {
      const response = await $api('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: {
          email,
          password,
          remember_me: remember,
        },
      })

      persistAuthPayload(response)

      return response
    }
    catch (err) {
      error.value = extractErrorMessage(err)
      throw err
    }
    finally {
      loading.value = false
    }
  }

  const fetchAuthenticatedUser = async () => {
    if (!accessTokenCookie.value)
      return null

    loading.value = true
    error.value = null

    try {
      const response = await $api('/api/auth/user', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (response) {
        userDataCookie.value = {
          ...(userDataCookie.value ?? {}),
          ...response,
        }

        if (response?.role)
          userRoleCookie.value = response.role
        
        // 🆕 Aggiorna i dati anestesista se presenti
        if (response?.anesthesiologist)
          anesthesiologistDataCookie.value = response.anesthesiologist
      }

      return response
    }
    catch (err) {
      error.value = extractErrorMessage(err)
      throw err
    }
    finally {
      loading.value = false
    }
  }

  const logout = async ({ redirect = true } = {}) => {
    if (!accessTokenCookie.value) {
      clearAuthPayload()
      
      return
    }

    loading.value = true
    error.value = null

    try {
      await $api('/api/auth/logout', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
    }
    catch (err) {
      error.value = extractErrorMessage(err)
      throw err
    }
    finally {
      clearAuthPayload()
      loading.value = false

      if (redirect)
        router.replace({ name: 'login' })
    }
  }

  const isAuthenticated = computed(() => Boolean(accessTokenCookie.value))
  const currentUser = computed(() => userDataCookie.value)
  const userRole = computed(() => userRoleCookie.value || userDataCookie.value?.role || '')
  const abilityRules = computed(() => userAbilitiesCookie.value ?? [])
  const userProfile = computed(() => userProfileCookie.value)
  const accessToken = computed(() => accessTokenCookie.value)
  
  // 🆕 Computed getter per i dati dell'anestesista
  const anesthesiologistData = computed(() => anesthesiologistDataCookie.value)
  
  // 🆕 Helper per ottenere le aree specialistiche
  const anesthesiologistSpecialistAreaIds = computed(() => 
    anesthesiologistDataCookie.value?.specialist_area_ids ?? [],
  )
  
  // 🆕 Helper per ottenere gli ospedali
  const anesthesiologistHospitalIds = computed(() =>
    anesthesiologistDataCookie.value?.hospital_ids ?? [],
  )

  return {
    loading: readonly(loading),
    error: readonly(error),
    isAuthenticated,
    currentUser,
    userRole,
    abilityRules,
    userProfile,
    accessToken,
    anesthesiologistData,
    anesthesiologistSpecialistAreaIds,
    anesthesiologistHospitalIds,
    login,
    logout,
    fetchAuthenticatedUser,
    clearAuthState: clearAuthPayload,
    clearError,
  }
})
